/**
 * ProductVariantSelector - Unified selector for products and variants
 * 
 * Allows selecting either:
 * - A product (meaning all variants of that product)
 * - Specific variant(s) from a product
 * 
 * Output format:
 * - { type: 'product', productId: 'xxx', productName: 'Product Name' }
 * - { type: 'variants', productId: 'xxx', variantIds: ['v1', 'v2'], variantNames: ['Variant 1', 'Variant 2'] }
 */

import { useState, useEffect, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Search, X, Package, ImageOff, ChevronDown, Check } from 'lucide-react';
import { cn } from "@/lib/utils";
import { useSearchProducts } from '@/lib/api/products';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { formatMoney } from "@/lib/money";

export interface ProductVariantSelection {
  type: 'product' | 'variants';
  productId: string;
  productName?: string;
  variantIds?: string[];
  variantNames?: string[];
}

interface ProductVariantSelectorProps {
  value: ProductVariantSelection | undefined;
  onChange: (value: ProductVariantSelection | undefined) => void;
  storeId?: string;
  placeholder?: string;
}

// Strip HTML tags from string
function stripHtml(html: string): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

export function ProductVariantSelector({
  value,
  onChange,
  storeId,
  placeholder = 'Search products...',
}: ProductVariantSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [openProducts, setOpenProducts] = useState<Set<string>>(new Set());

  // Debounced search update
  const updateDebouncedSearch = useDebouncedCallback((query: string) => {
    setDebouncedSearch(query);
  }, 300);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    updateDebouncedSearch(query);
  };

  // Load all products
  const { data: allProductsData, isLoading: isLoadingAll } = useSearchProducts(
    { storeId: storeId || null, productId: null, searchValue: null },
    { enabled: !!storeId && !debouncedSearch },
  );

  // Search products
  const shouldSearch = !!storeId && !!debouncedSearch && debouncedSearch.trim().length > 0;
  const { data: searchResults, isLoading: isSearching } = useSearchProducts(
    { storeId: storeId || null, productId: null, searchValue: debouncedSearch || null },
    { enabled: shouldSearch },
  );

  const products = shouldSearch && searchResults ? searchResults : (allProductsData || []);
  const loading = shouldSearch ? isSearching : isLoadingAll;

  // Auto-expand product with selection
  useEffect(() => {
    if (value?.productId && products.length > 0) {
      setOpenProducts(prev => new Set([...prev, value.productId]));
    }
  }, [value?.productId, products]);

  const toggleProduct = (productId: string) => {
    setOpenProducts(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  // Check if a product is selected (all variants)
  const isProductSelected = (productId: string) => {
    return value?.type === 'product' && value?.productId === productId;
  };

  // Check if specific variant is selected
  const isVariantSelected = (productId: string, variantId: string) => {
    return (
      value?.type === 'variants' &&
      value?.productId === productId &&
      value?.variantIds?.includes(variantId)
    );
  };

  // Handle selecting entire product
  const selectProduct = (product: any) => {
    if (isProductSelected(product.id)) {
      onChange(undefined);
    } else {
      onChange({
        type: 'product',
        productId: product.id,
        productName: product.name,
      });
    }
  };

  // Handle selecting/deselecting a variant
  const toggleVariant = (product: any, variant: any) => {
    const currentVariants = value?.type === 'variants' && value?.productId === product.id
      ? value.variantIds || []
      : [];

    const currentNames = value?.type === 'variants' && value?.productId === product.id
      ? value.variantNames || []
      : [];

    if (currentVariants.includes(variant.id)) {
      // Remove variant
      const newVariants = currentVariants.filter(id => id !== variant.id);
      const newNames = currentNames.filter((_, i) => currentVariants[i] !== variant.id);
      
      if (newVariants.length === 0) {
        onChange(undefined);
      } else {
        onChange({
          type: 'variants',
          productId: product.id,
          productName: product.name,
          variantIds: newVariants,
          variantNames: newNames,
        });
      }
    } else {
      // Add variant (clear product selection if was selected)
      onChange({
        type: 'variants',
        productId: product.id,
        productName: product.name,
        variantIds: [...currentVariants.filter(() => value?.productId === product.id), variant.id],
        variantNames: [...currentNames.filter(() => value?.productId === product.id), variant.name],
      });
    }
  };

  // Get price info
  const getPriceInfo = (variant: any): { amount: number; currency: string } | null => {
    if (variant.prices && variant.prices.length > 0) {
      const price = variant.prices[0];
      const currencies = Object.keys(price.currencyOptions || {});
      if (currencies.length > 0) {
        const currency = currencies[0];
        const currencyData = price.currencyOptions[currency];
        if (currencyData && typeof currencyData.amount === 'number') {
          return { amount: currencyData.amount, currency: currency.toUpperCase() };
        }
      }
    }
    return null;
  };

  if (loading) {
    return (
      <div className="w-full min-w-0 space-y-2">
        {[1, 2].map(i => (
          <div key={i} className="animate-pulse rounded-md border p-2">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded bg-gray-200" />
              <div className="h-4 w-32 rounded bg-gray-200" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 space-y-2 overflow-hidden">
      {/* Current Selection Summary */}
      {value && (
        <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 p-2 dark:border-green-800 dark:bg-green-900/20">
          <Check className="h-3.5 w-3.5 shrink-0 text-green-600" />
          <div className="min-w-0 flex-1 text-xs">
            {value.type === 'product' ? (
              <span className="font-medium text-green-800 dark:text-green-300">
                All variants of "{value.productName}"
              </span>
            ) : (
              <div className="flex flex-wrap gap-1">
                <span className="text-green-700 dark:text-green-400">
                  {value.variantNames?.length || 0} variant(s) from "{value.productName}":
                </span>
                {value.variantNames?.map((name, i) => (
                  <Badge key={i} variant="secondary" className="h-5 text-[10px]">
                    {name}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0 text-green-600 hover:text-red-500"
            onClick={() => onChange(undefined)}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
        <Input
          type="text"
          placeholder={placeholder}
          value={searchQuery}
          onChange={handleSearchChange}
          className="h-8 pl-8 pr-8 text-xs"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => { setSearchQuery(''); setDebouncedSearch(''); }}
            className="text-muted-foreground hover:text-foreground absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Product List */}
      <div className="max-h-56 space-y-1 overflow-y-auto">
        {products.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-md border border-dashed p-4">
            <Package className="text-muted-foreground mb-1 h-5 w-5" />
            <p className="text-muted-foreground text-xs">
              {debouncedSearch ? 'No match' : 'No products'}
            </p>
          </div>
        ) : (
          products.map((product: any) => {
            const isOpen = openProducts.has(product.id);
            const variantCount = product.variants?.length || 0;
            const productSelected = isProductSelected(product.id);
            const hasVariantSelection = value?.type === 'variants' && value?.productId === product.id;
            const selectedVariantCount = hasVariantSelection ? (value.variantIds?.length || 0) : 0;
            const firstImage = product.variants?.find((v: any) => v.imageUrl)?.imageUrl;

            return (
              <Collapsible key={product.id} open={isOpen} onOpenChange={() => toggleProduct(product.id)}>
                <div className={cn(
                  'w-full min-w-0 overflow-hidden rounded-md border transition-colors',
                  (productSelected || hasVariantSelection) && 'border-green-300 bg-green-50/50 dark:border-green-700 dark:bg-green-900/10'
                )}>
                  {/* Product Header */}
                  <div className="flex w-full min-w-0 items-center gap-2 p-2">
                    {/* Select All Checkbox */}
                    <Checkbox
                      checked={productSelected}
                      onCheckedChange={() => selectProduct(product)}
                      className="h-4 w-4 shrink-0"
                    />

                    {/* Expand Trigger */}
                    <CollapsibleTrigger className="flex min-w-0 flex-1 items-center gap-2">
                      {firstImage ? (
                        <img src={firstImage} alt={product.name} className="h-7 w-7 shrink-0 rounded object-cover" />
                      ) : (
                        <div className="bg-muted flex h-7 w-7 shrink-0 items-center justify-center rounded">
                          <ImageOff className="text-muted-foreground h-3.5 w-3.5" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1 overflow-hidden text-left">
                        <h3 className="truncate text-xs font-medium leading-tight">{product.name}</h3>
                        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                          <span>{variantCount} variants</span>
                          {selectedVariantCount > 0 && (
                            <Badge variant="secondary" className="h-4 px-1 text-[9px]">
                              {selectedVariantCount} selected
                            </Badge>
                          )}
                        </div>
                      </div>
                      <ChevronDown className={cn(
                        'text-muted-foreground h-3.5 w-3.5 shrink-0 transition-transform',
                        isOpen && 'rotate-180'
                      )} />
                    </CollapsibleTrigger>
                  </div>

                  {/* Variants */}
                  <CollapsibleContent>
                    <div className="space-y-1 border-t bg-slate-50/50 p-2 dark:bg-slate-900/50">
                      <p className="mb-1 text-[10px] font-medium text-muted-foreground">
                        Select specific variants (or check product above for all)
                      </p>
                      {product.variants?.map((variant: any) => {
                        const selected = isVariantSelected(product.id, variant.id);
                        const priceInfo = getPriceInfo(variant);
                        return (
                          <label
                            key={variant.id}
                            className={cn(
                              'flex w-full min-w-0 cursor-pointer items-center gap-2 overflow-hidden rounded-md border p-1.5 transition-all',
                              'hover:border-primary/50 hover:bg-accent',
                              selected && 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/30',
                              productSelected && 'opacity-50'
                            )}
                          >
                            <Checkbox
                              checked={selected || productSelected}
                              disabled={productSelected}
                              onCheckedChange={() => toggleVariant(product, variant)}
                              className="h-3.5 w-3.5 shrink-0"
                            />
                            {variant.imageUrl ? (
                              <img src={variant.imageUrl} alt={variant.name} className="h-6 w-6 shrink-0 rounded object-cover" />
                            ) : (
                              <div className="bg-muted flex h-6 w-6 shrink-0 items-center justify-center rounded">
                                <ImageOff className="text-muted-foreground h-3 w-3" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1 overflow-hidden">
                              <span className="block truncate text-[11px] font-medium leading-tight">{variant.name}</span>
                              {priceInfo && (
                                <span className="text-primary text-[10px] font-semibold">
                                  {formatMoney(priceInfo.amount, priceInfo.currency)}
                                </span>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            );
          })
        )}
      </div>
    </div>
  );
}







